import { useCallback, useRef, useEffect, type FC, type ReactNode } from "react";
import { createPortal } from "react-dom";

/**
 * Options for configuring the print behavior
 */
export interface UsePrintNodeOptions {
  /**
   * Whether to hide the print node when not printing.
   * When true, the content is only visible during print.
   * When false, the content is visible both on screen and when printing.
   * @default true
   */
  hideWhenNotPrinting?: boolean;

  /**
   * Additional CSS to inject for print media queries.
   * This will be added to the injected style tag.
   * @example
   * ```typescript
   * printStyles: `
   *   @page {
   *     margin: 2cm;
   *     size: A4 landscape;
   *   }
   * `
   * ```
   */
  printStyles?: string;
}

/**
 * Return type for the usePrintNode hook
 */
export interface UsePrintNodeReturn {
  /**
   * Function to trigger the browser's print dialog.
   * Call this to print the content wrapped in PrintPortal.
   */
  print: () => void;

  /**
   * Unique identifier generated for this print node.
   * Used internally for the data-printid attribute.
   */
  printId: string;

  /**
   * Portal component that renders the printable content.
   * Wrap your printable content with this component.
   * @example
   * ```tsx
   * <PrintPortal>
   *   <YourContentToPrint />
   * </PrintPortal>
   * ```
   */
  PrintPortal: FC<{ children: React.ReactNode }>;
}

/**
 * Hook for printing specific React elements with isolated print styles.
 *
 * This hook provides a simple API for printing content by:
 * - Generating a unique ID to avoid conflicts with other printable elements
 * - Optionally hiding the content when not printing
 * - Injecting print-specific styles that only affect the targeted element
 * - Using React portals to mount content to document.body
 *
 * @param options - Configuration options for print behavior
 * @returns Object containing print function, print ID, and PrintPortal component
 *
 * @example
 * Basic usage:
 * ```tsx
 * function MyComponent() {
 *   const { print, PrintPortal } = usePrintNode();
 *
 *   return (
 *     <>
 *       <Button onClick={print}>Print</Button>
 *
 *       <PrintPortal>
 *         <div>Content to print</div>
 *       </PrintPortal>
 *     </>
 *   );
 * }
 * ```
 *
 * @example
 * Multiple printable elements:
 * ```tsx
 * function DocumentManager() {
 *   const invoice = usePrintNode();
 *   const receipt = usePrintNode();
 *
 *   return (
 *     <>
 *       <Button onClick={invoice.print}>Print Invoice</Button>
 *       <Button onClick={receipt.print}>Print Receipt</Button>
 *
 *       <invoice.PrintPortal>
 *         <InvoiceDocument />
 *       </invoice.PrintPortal>
 *
 *       <receipt.PrintPortal>
 *         <ReceiptDocument />
 *       </receipt.PrintPortal>
 *     </>
 *   );
 * }
 * ```
 *
 * @example
 * Visible preview with custom print styles:
 * ```tsx
 * function ReportPreview() {
 *   const { print, PrintPortal } = usePrintNode({
 *     hideWhenNotPrinting: false,
 *     printStyles: `
 *       @page {
 *         margin: 2cm;
 *         size: A4 landscape;
 *       }
 *     `,
 *   });
 *
 *   return (
 *     <>
 *       <Button onClick={print}>Print Report</Button>
 *
 *       <PrintPortal>
 *         <ReportContent />
 *       </PrintPortal>
 *     </>
 *   );
 * }
 * ```
 */
export const usePrintNode = (options: UsePrintNodeOptions = {}): UsePrintNodeReturn => {
  const { hideWhenNotPrinting = true, printStyles = "" } = options;

  // Generate a stable unique ID for this print node
  const printId = useRef(crypto.randomUUID()).current;

  // Memoized print function that triggers the browser print dialog
  const print = useCallback(() => {
    window.print();
  }, []);

  // Inject and manage print styles
  useEffect(() => {
    const styleId = `print-styles-${printId}`;
    let styleElement = document.getElementById(styleId) as HTMLStyleElement | null;

    if (!styleElement) {
      styleElement = document.createElement("style");
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    // Build the print media query styles
    const baseStyles = `
      @media print {
        /* Hide all elements except the one with our unique print ID */
        body > *:not([data-printid="${printId}"]) {
          display: none !important;
        }

        /* Ensure our print element is visible */
        [data-printid="${printId}"] {
          display: block !important;
        }
      }
    `;

    styleElement.textContent = baseStyles + (printStyles ? `\n${printStyles}` : "");

    // Cleanup: remove the style element when component unmounts
    return () => {
      styleElement?.remove();
    };
  }, [printId, printStyles]);

  // Create the PrintPortal component
  const PrintPortal = useCallback<FC<{ children: ReactNode }>>(
    ({ children }) => {
      const className = hideWhenNotPrinting ? "hidden print:block" : "print:block";

      const content = (
        <div data-printid={printId} className={className}>
          {children}
        </div>
      );

      return hideWhenNotPrinting ? (
        createPortal(content, document.body)
      ) : (
        <>
          {content}
          {createPortal(content, document.body)}
        </>
      );
    },
    [printId, hideWhenNotPrinting]
  );

  return {
    print,
    printId,
    PrintPortal,
  };
};
